import {
  ApolloClient,
  HttpLink,
  ApolloLink,
  Observable,
  gql,
} from '@apollo/client';
import { onError } from '@apollo/link-error';
import Cookies from 'js-cookie';
import refreshAuthenticationMutation from '../graphql/mutations/refreshAuthenticationMutation';
import authenticationQuery from '../graphql/queries/authenticationQuery';
import journeyStatus from '../graphql/computed/journeyStatus';
import journeyUpNext from '../graphql/computed/journeyUpNext';
import meQuery from '../graphql/queries/meQuery';
import getAuthenticationToken from '../utils/getAuthenticationToken';
import produce from 'immer';
import ApolloLinkTimeout from 'apollo-link-timeout';
import makeAuthenticationCookieOptions
  from '../utils/makeAuthenticationCookieOptions';
import cache from './cache';

/**
 * Upgrade, version by version, the user's preferences stored in its
 * localStorage to the current preferences' version.
 *
 * This is useful when there are "breaking-changes" to the value of the user's
 * preferences. Here we have the chance to correctly handle them.
 *
 * @param defaultItem
 * @param localStorageItem
 * @returns {*}
 */
function maybeUpgradeUserPreferences(defaultItem, localStorageItem) {
  if (localStorageItem.version === defaultItem.version) {
    return localStorageItem;
  }

  switch (localStorageItem.version) {
    case undefined:
      return maybeUpgradeUserPreferences(defaultItem, {
        ...localStorageItem,
        version: 1,
      });

    // This example case will handle the upgrade from version 1 to 2:
    // case 1:
    //   return maybeUpgradeUserPreferences(defaultItem, {
    //     ...localStorageItem,
    //     version: 2,
    //     ...we can modify the to-be preferences here.
    //   });

    default:
      return maybeUpgradeUserPreferences(defaultItem, {
        ...localStorageItem,
        version: localStorageItem.version + 1,
      });
  }
}

const authLink = new ApolloLink((operation, forward) => {
  const cookieAuthenticationToken = getAuthenticationToken({ checkExpiration: false });

  if (cookieAuthenticationToken !== undefined) {
    operation.setContext(({ headers, ...context }) => ({
      ...context,
      headers: { ...headers, authorization: `Bearer ${cookieAuthenticationToken}` },
    }));
  }

  return forward(operation);
});

const errorLink = onError(({
  forward,
  graphQLErrors,
  networkError = {},
  operation,
  response,
}) => {
  if (graphQLErrors) {
    for (const graphQLError of graphQLErrors) {
      // @todo 2020-04-07 "Unauthenticated." seems quite a weak check
      if (graphQLError.message === 'Unauthenticated.') {
        const cookieAuthenticationToken = getAuthenticationToken({ checkExpiration: false });

        if (cookieAuthenticationToken !== undefined) {
          return new Observable(observer => {
            client.mutate({ mutation: refreshAuthenticationMutation }).then(({ data: { refreshAuthentication } }) => {
              operation.setContext(({ headers, ...context }) => ({
                ...context,
                headers: { ...headers, authorization: `Bearer ${refreshAuthentication.token}` },
              }));
              forward(operation).subscribe(observer);
            }).catch(error => {
              // When the refresh token mutation errors out (which usually means the
              // token is not usable anymore) we clear the token and refresh the page.
              console.error(error);

              Cookies.remove('authentication-token');
              window.location.reload();
            });
          });
        }
      }

      // @todo 2020-05-14 temporary, improve
      if (graphQLError.debugMessage === 'The token has been blacklisted') {
        Cookies.remove('authentication-token');
        window.location.reload();
      }

      // @todo 2020-05-14 temporary, remove
      if (graphQLError.debugMessage === 'Token Signature could not be verified.') {
        Cookies.remove('authentication-token');
        window.location.reload();
      }
    }
  }

  if (networkError) { // @todo 2020-04-08
    console.error(`[Network error]`, networkError, response);
  }
});

const httpLink = new HttpLink({
  uri: process.env.REACT_APP_GRAPHQL_ENDPOINT,
});

const timeoutLink = new ApolloLinkTimeout(0); // in general no timeout

const client = new ApolloClient({
  cache: cache,
  link: ApolloLink.from([errorLink, authLink, timeoutLink, httpLink]),
  resolvers: {
    Journey: {
      status: journey => journeyStatus(journey),
      upNext: journey => journeyUpNext(journey),
    },
    Step: {
      // @todo 2021-04-07
      //   This should be possible to do via API rather than only client-side
      journeyStepPivot: step => {
        return step.journeyStepPivot !== undefined
          ? step.journeyStepPivot
          : step.journeys[0].journeyStepPivot;
      },
    },
    User: {
      preferences: (me, _args, { cache }) => {
        const key = cache.identify({ __typename: 'UserPreferences', id: me.id });

        const localStorageItem = window.localStorage.getItem(key);

        // We get this value from LocalStorage because it might have been set
        // before the user login.
        const soundtrackMuted = window.localStorage.getItem('soundtrack-muted') === 'true';

        const defaultItem = {
          id: me.id,
          version: 1, // @todo 2020-07-22 use this to migrate localStorage from version to version when needed
          audioMuted: false,
          soundtrackMuted,
          textTrackLanguage: null,
          lastViewedContent: null,
          hotspotTutorialOptOut: false,
          equirectangularPictureGyroscopeTutorialOptOut: false,
          __typename: 'UserPreferences'
        };

        if (localStorageItem === null) {
          return defaultItem;
        }

        return {
          ...defaultItem,
          ...maybeUpgradeUserPreferences(defaultItem, JSON.parse(localStorageItem)),
        };
      }
    },
    Query: {
      authentication: () => {
        const cookieAuthenticationToken = getAuthenticationToken();

        return {
          id: 'authentication',
          token: cookieAuthenticationToken !== undefined ? cookieAuthenticationToken : null,
          __typename: 'Authentication',
        }
      },
    },
    Mutation: {
      updatePreferencesMutation: (rootValue, args, { cache }) => {
        const { me } = cache.readQuery({ query: meQuery });

        // @todo 2020-07-22 consider extracting this.
        const keysWhitelist = [
          'audioMuted',
          'soundtrackMuted',
          'textTrackLanguage',
          'lastViewedContent',
          'hotspotTutorialOptOut',
          'equirectangularPictureGyroscopeTutorialOptOut',
        ];

        const preferences = produce(me.preferences, draft => {
          Object.keys(args).filter(key => (
            args[key] !== undefined &&
            keysWhitelist.indexOf(key) !== -1
          )).forEach(key => {
            draft[key] = args[key];
          });
        });

        const key = cache.identify({ __typename: 'UserPreferences', id: me.id });

        window.localStorage.setItem(
          key,
          JSON.stringify(preferences)
        );

        // We need to manually update the Apollo cache right after updating the
        // localStorage, otherwise there could be cases where the old values are
        // used to derive the next values, overriding effectively the latest
        // values.
        cache.writeFragment({
          id: key,
          data: preferences,
          fragment: gql`
            fragment PreferencesUpdate on UserPreferences {
              ${Object.keys(preferences).join(', ')}
            }
          `,
        });

        return preferences;
      }
    },
  }
});

client.watchQuery({
  query: authenticationQuery
}).subscribe({
  next: value => {
    if (value.data.authentication.token === null) {
      Cookies.remove('authentication-token');
    // We get a valid token, and we check whether the application is
    // configured to use an SSO, if so we do not need to set the auth cookie.
    } else {
      Cookies.set(
        'authentication-token',
        value.data.authentication.token,
        makeAuthenticationCookieOptions(value.data.authentication.token)
      );
    }
  },
  error: error => console.error(error),
});

export default function createClient() {
  return client;
};
